import React, { useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  CircularProgress, 
  Button, 
  Snackbar, 
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { Check, Close, Visibility, Download, Delete } from '@mui/icons-material';
import CustomAppBar from '../components/appbar';
import axios from 'axios';

const linkurl = process.env.REACT_APP_EXPRESS_SERVER;

const PaymentManagement = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentReceipt, setCurrentReceipt] = useState(null);

    const tabLabels = ['REQUESTED', 'ACCEPTED', 'REJECTED'];

    useEffect(() => {
        const fetchPayments = async () => {
            try {
                const response = await axios.get(`${linkurl}/payments`);
                console.log(response.data);
                setPayments(response.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, []);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await axios.patch(`${linkurl}/payments/${id}`, { status: newStatus });
            
            setPayments(prevPayments => 
                prevPayments.map(payment => 
                    payment.id === id ? { ...payment, status: newStatus } : payment
                )
            );
            
            setSnackbar({
                message: `Payment ${newStatus.toLowerCase()} successfully!`,
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                message: err.response?.data?.error || 'Failed to update payment status',
                severity: 'error'
            });
        }
    };

    const handleDeletePayment = async (id) => {
        try {
            await axios.delete(`${linkurl}/payments/${id}`);
            
            setPayments(prevPayments => 
                prevPayments.filter(payment => payment.id !== id)
            );
            
            setSnackbar({
                message: 'Payment deleted successfully!',
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                message: err.response?.data?.error || 'Failed to delete payment',
                severity: 'error'
            });
        }
    };

    const handleViewReceipt = (payment) => {
        setCurrentReceipt(payment);
        setDialogOpen(true);
    };

    const handleDownloadReceipt = () => {
        if (currentReceipt) {
            const link = document.createElement('a');
            link.href = `${linkurl}${currentReceipt.Link}`;
            link.download = `receipt_${currentReceipt.RFPNo}.${currentReceipt.Link.split('.').pop()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(null);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentReceipt(null);
    };

    const filteredPayments = payments.filter(payment => 
        payment.status === tabLabels[currentTab]
    );

    if (loading) {
        return (
            <Box>
                <CustomAppBar active="payments" />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <CustomAppBar active="payments" />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography color="error">{error}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <CustomAppBar active="payments" />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Payment Receipt Management
                    </Typography>

                    <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                        {tabLabels.map((label, index) => (
                            <Tab 
                                key={label} 
                                label={`${label} (${payments.filter(p => p.status === label).length})`} 
                                id={`tab-${index}`}
                                aria-controls={`tabpanel-${index}`}
                            />
                        ))}
                    </Tabs>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>RFP No</TableCell>
                                    <TableCell>Vendor</TableCell>
                                    <TableCell>Uploaded By</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredPayments.length > 0 ? (
                                    filteredPayments.map((payment) => (
                                        <TableRow key={payment.id}>
                                            <TableCell>{payment.RFPNo}</TableCell>
                                            <TableCell>{payment.Vendor}</TableCell>
                                            <TableCell>{payment.UserName}</TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={1}>
                                                    <Tooltip title="View Receipt">
                                                        <IconButton 
                                                            color="primary" 
                                                            onClick={() => handleViewReceipt(payment)}
                                                        >
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    <Tooltip title="Delete">
                                                        <IconButton 
                                                            color="error" 
                                                            onClick={() => handleDeletePayment(payment.id)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    {payment.status === 'REQUESTED' && (
                                                        <>
                                                            <Tooltip title="Accept">
                                                                <IconButton 
                                                                    color="success" 
                                                                    onClick={() => handleUpdateStatus(payment.id, 'ACCEPTED')}
                                                                >
                                                                    <Check />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton 
                                                                    color="error" 
                                                                    onClick={() => handleUpdateStatus(payment.id, 'REJECTED')}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            No {tabLabels[currentTab].toLowerCase()} payments found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>

            {/* Receipt View Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Receipt for RFP: {currentReceipt?.RFPNo}
                    <Typography variant="subtitle2">
                        Vendor: {currentReceipt?.Vendor}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {currentReceipt && (() => {
                        const link = document.createElement('a');
                        link.href = `${linkurl}${currentReceipt.Link}`;
                        link.download = `receipt_${currentReceipt.RFPNo}.${currentReceipt.Link.split('.').pop()}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        return currentReceipt.Link.endsWith('.pdf') ? (
                            <iframe 
                                src={`${linkurl}${currentReceipt.Link}`} 
                                style={{ width: '100%', height: '70vh', border: 'none' }}
                                title="PDF Viewer"
                            />
                        ) : (
                            <Box 
                                component="img" 
                                src={`${linkurl}${currentReceipt.Link}`} 
                                alt="Receipt" 
                                sx={{ 
                                    width: '100%', 
                                    height: 'auto',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
            {/* Snackbar for notifications */}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar?.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PaymentManagement;